import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '../../billing-host/errors';
import { invalidateAfterSale } from '../../lib/invalidate-after-sale';
import { NEVER_QUEUED, OFFLINE_REPLAYED, sendBillingMutation } from '../../lib/billing-mutations';
import { invoiceReferenceFileStem, type InvoicePaymentMethodCode, type TaxGroupCode } from '@efacture/fiscal-core';
import type {
  InvoiceWire,
  IssuedInvoiceWire,
  InvoiceLineWire,
  InvoicePaymentWire,
  InvoiceListItemWire,
  InvoiceListResponseWire,
} from '@efacture/fiscal-core';
import type { BillingTransport } from '../../billing-host/types';
import { useBillingApi } from '../../billing-host/api';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────
//
// The response shapes are the shared contracts from @efacture/core
// (docs/ARCHITECTURE.md, "API↔web response contracts"), aliased under
// this module's names.

export type InvoiceType = 'FV' | 'FA' | 'FT' | 'EV' | 'ET' | 'EA';
/** Code of the credit-note nature table (DGI COR/RAN/RAM/RRR plus the tenant's rows, §2.35). */
export type CreditNoteType = string;
export type InvoiceStatus = 'draft' | 'pending' | 'approved' | 'signed' | 'cancelled';
export type PaymentStatus = 'PENDING' | 'PARTIAL' | 'PAID' | 'OVERDUE';
export type ClientType = string;
export type PriceMode = 'HT' | 'TTC';
export type TaxGroup = TaxGroupCode;

export type InvoiceLine = InvoiceLineWire;
export type Payment = InvoicePaymentWire;
export type Invoice = InvoiceWire;
/** Reply of `POST /api/invoices/:id/sign`: may carry the MCF taxpayer mismatch (`mcfTaxpayerWarning`). */
export type IssuedInvoice = IssuedInvoiceWire;

export interface InvoiceListParams {
  page?: number | undefined;
  limit?: number | undefined;
  status?: InvoiceStatus | undefined;
  type?: InvoiceType | undefined;
  paymentStatus?: PaymentStatus | undefined;
  clientId?: string | undefined;
  search?: string | undefined;
  startDate?: string | undefined;
  endDate?: string | undefined;
  /** Multi-établissement (Tâche 10) : filtre optionnel par site facturant. */
  establishmentId?: string | undefined;
  /** Only the debit notes. */
  debitNotes?: boolean | undefined;
}

/** A row of GET /invoices: the invoice without its lines (read them via useInvoice). */
export type InvoiceListItem = InvoiceListItemWire;
export type InvoiceListResponse = InvoiceListResponseWire;

export interface InvoiceLineInput {
  articleId: string;
  articleCode: string;
  articleName: string;
  articleType: string;
  taxGroup: TaxGroup;
  psvbGroup?: string | undefined;
  /** TSB 10% — Taxe Spéciale sur les Boissons (DGI §6.8), dénormalisé depuis l'article. */
  isBeverage?: boolean | undefined;
  quantity: number;
  unitPrice: number;
  /** Catalogue unit price in the invoice's price mode when the line was added, for verification audit. */
  articleCatalogPrice?: number | undefined;
  discount?: number | undefined;
}

/**
 * One comment line printed on the invoice (DGI §2.27). `type` is a code of
 * the annex table « Lignes de commentaires » (A-H) and supplies the label.
 */
export interface CommentLineInput {
  type: string;
  content: string;
}

/**
 * Informative counter-value in a foreign currency (#146): printed on the
 * signed invoice, never sent to the MCF. Without `rate`, the fixed euro
 * parity or the latest stored rate applies.
 */
export interface CounterValueInput {
  currency: string;
  /** XOF per unit, typed by the user. */
  rate?: number | undefined;
}

/** The rate a counter-value takes when none is typed (GET /invoices/counter-value-rate). */
export interface CounterValueRate {
  currency: string;
  rate: number;
  rateDate: string;
  /** `PARITE_FIXE`, `MANUEL` or the key of the provider the rate was quoted by. */
  source: string;
}

export interface CreateInvoiceInput {
  type: InvoiceType;
  clientType: ClientType;
  clientId?: string | undefined;
  clientName?: string | undefined;
  clientIfu?: string | undefined;
  priceMode: PriceMode;
  creditNoteType?: CreditNoteType | undefined;
  notes?: string | undefined;
  commentLines?: CommentLineInput[] | undefined;
  /**
   * Settlements received at issue. The API requires their sum to equal the
   * computed total TTC, records them, marks the invoice PAID and derives
   * the quittance stamp duty from them (CGI art. 519/520).
   */
  payments?: RecordPaymentInput[] | undefined;
  counterValue?: CounterValueInput | undefined;
  lines: InvoiceLineInput[];
}

export interface UpdateInvoiceInput {
  notes?: string | undefined;
  commentLines?: CommentLineInput[] | undefined;
  lines?: InvoiceLineInput[] | undefined;
  /**
   * Settlements at issue. A draft that holds some must resend them with new
   * lines; an empty list removes them.
   */
  payments?: RecordPaymentInput[] | undefined;
  /** Absent keeps the stored counter-value; null removes it. */
  counterValue?: CounterValueInput | null | undefined;
}

/**
 * A credit note issued without an original invoice (DGI §2.29). The API
 * accepts the nature RRR only and reads the client from its records.
 */
export interface CreateStandaloneCreditNoteInput {
  type: 'FA' | 'EA';
  creditNoteType: 'RRR';
  clientId: string;
  priceMode: PriceMode;
  establishmentId?: string | undefined;
  notes?: string | undefined;
  commentLines?: CommentLineInput[] | undefined;
  lines: InvoiceLineInput[];
}

export interface RecordPaymentInput {
  amount: number;
  method: InvoicePaymentMethodCode;
  reference?: string | undefined;
  /** AVOIR: the signed credit note the payment applies. */
  creditNoteId?: string | undefined;
}

/** A signed credit note a payment can apply, with its balance not yet applied. */
export interface ApplicableCreditNote {
  id: string;
  number: string;
  signedAt: string | null;
  originalInvoiceId: string | null;
  available: number;
}

// ─────────────────────────────────────────────────────────────
// Query Keys
// ─────────────────────────────────────────────────────────────

export const invoiceKeys = {
  all: ['invoices'] as const,
  lists: () => [...invoiceKeys.all, 'list'] as const,
  list: (params: InvoiceListParams) => [...invoiceKeys.lists(), params] as const,
  details: () => [...invoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...invoiceKeys.details(), id] as const,
  byNumber: (number: string) => [...invoiceKeys.all, 'byNumber', number] as const,
  byClient: (clientId: string) => [...invoiceKeys.all, 'byClient', clientId] as const,
  statistics: () => [...invoiceKeys.all, 'statistics'] as const,
  overdue: () => [...invoiceKeys.all, 'overdue'] as const,
  applicableCreditNotes: (id: string) => [...invoiceKeys.all, 'applicableCreditNotes', id] as const,
  counterValueRate: (currency: string) => [...invoiceKeys.all, 'counterValueRate', currency] as const,
};

// ─────────────────────────────────────────────────────────────
// API Functions
// ─────────────────────────────────────────────────────────────

async function fetchInvoices(api: BillingTransport, params: InvoiceListParams): Promise<InvoiceListResponse> {
  return api.get<InvoiceListResponse>('/invoices', {
    page: params.page,
    limit: params.limit,
    status: params.status,
    type: params.type,
    paymentStatus: params.paymentStatus,
    search: params.search,
    clientId: params.clientId,
    startDate: params.startDate,
    endDate: params.endDate,
    establishmentId: params.establishmentId,
    debitNotes: params.debitNotes ? 'true' : undefined,
  });
}

async function fetchInvoice(api: BillingTransport, id: string): Promise<Invoice> {
  return api.get<Invoice>(`/invoices/${id}`);
}

async function fetchCounterValueRate(
  api: BillingTransport,
  currency: string
): Promise<{ counterValueRate: CounterValueRate | null }> {
  return api.get<{ counterValueRate: CounterValueRate | null }>('/invoices/counter-value-rate', { currency });
}

async function fetchInvoiceByNumber(api: BillingTransport, number: string): Promise<Invoice> {
  return api.get<Invoice>(`/invoices/by-number/${encodeURIComponent(number)}`);
}

async function fetchInvoicesByClient(
  api: BillingTransport, clientId: string,
  params: { page?: number; limit?: number }
): Promise<InvoiceListResponse> {
  return api.get<InvoiceListResponse>(`/invoices/client/${clientId}`, params);
}

async function createInvoice(api: BillingTransport, input: CreateInvoiceInput): Promise<Invoice> {
  return sendBillingMutation<Invoice>(api, 'POST', '/invoices', input);
}

async function updateInvoice(api: BillingTransport, id: string, input: UpdateInvoiceInput): Promise<Invoice> {
  return sendBillingMutation<Invoice>(api, 'PUT', `/invoices/${id}`, input);
}

async function deleteInvoice(api: BillingTransport, id: string): Promise<void> {
  await sendBillingMutation(api, 'DELETE', `/invoices/${id}`);
}

async function signInvoice(api: BillingTransport, id: string): Promise<IssuedInvoice> {
  return sendBillingMutation<IssuedInvoice>(api, 'POST', `/invoices/${id}/sign`);
}

/** A fiscal number cancelled after the MCF refused it, POST /api/invoices/:id/cancel-refused-number. */
export interface CancelledRefusedNumber {
  number: string;
  invoiceType: string;
  fiscalYear: number;
  establishmentId: string;
  reason: string;
  cancelledAt: string;
  invoiceReference: string;
  /** « Numéro présenté au MCF, refusé, annulé ». */
  label: string;
}

async function cancelRefusedNumber(api: BillingTransport, id: string, reason: string): Promise<CancelledRefusedNumber> {
  const result = await sendBillingMutation<{ cancelled: CancelledRefusedNumber }>(
    api, 'POST',
    `/invoices/${id}/cancel-refused-number`,
    { reason }
  );
  return result.cancelled;
}

async function fetchApplicableCreditNotes(api: BillingTransport, id: string): Promise<ApplicableCreditNote[]> {
  const response = await api.get<{ creditNotes: ApplicableCreditNote[] }>(
    `/invoices/${id}/applicable-credit-notes`
  );
  return response.creditNotes;
}

/**
 * Hook: signed credit notes that can settle an invoice
 * GET /api/invoices/:id/applicable-credit-notes
 */
export function useApplicableCreditNotes(id: string, options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: invoiceKeys.applicableCreditNotes(id),
    queryFn: () => fetchApplicableCreditNotes(api, id),
    enabled: (options?.enabled ?? true) && !!id,
  });
}

async function recordPayment(api: BillingTransport, id: string, input: RecordPaymentInput): Promise<Invoice> {
  return sendBillingMutation<Invoice>(api, 'POST', `/invoices/${id}/payments`, input);
}

async function cancelPayment(api: BillingTransport, id: string, paymentId: string, reason: string): Promise<Invoice> {
  return sendBillingMutation<Invoice>(api, 'POST', `/invoices/${id}/payments/${paymentId}/cancel`, { reason });
}

async function fetchOverdueInvoices(api: BillingTransport): Promise<InvoiceListItem[]> {
  // Fetch invoices that are signed but not fully paid
  const response = await api.get<InvoiceListResponse>('/invoices', {
    status: 'signed',
    limit: 100,
  });
  // Filter for unpaid or partially paid invoices
  return response.invoices.filter(
    (invoice) => invoice.paymentStatus === 'PENDING' || invoice.paymentStatus === 'PARTIAL'
  );
}

async function downloadInvoicePdf(api: BillingTransport, id: string, invoiceNumber: string): Promise<void> {
  const filename = `${invoiceReferenceFileStem(invoiceNumber)}.pdf`;
  await api.download(`/invoices/${id}/pdf`, filename);
}

/** Document a preview shows: the A4 PDF or the till ticket's HTML at a paper width. */
export type InvoicePreviewFormat = 'A4' | 'TICKET_58' | 'TICKET_80';

/**
 * The watermarked preview « APERÇU — ne vaut pas facture » (rule B).
 * GET /api/invoices/:id/preview — records nothing, so viewing never consumes
 * the original. Read for an in-app viewer only, never cached.
 */
export function fetchInvoicePreview(
  api: BillingTransport, id: string,
  format: InvoicePreviewFormat,
  signal?: AbortSignal
): Promise<Blob> {
  return api.blob(`/invoices/${encodeURIComponent(id)}/preview?format=${format}`, signal);
}

// ─────────────────────────────────────────────────────────────
// Queries
// ─────────────────────────────────────────────────────────────

/**
 * Hook to fetch a paginated list of invoices
 * GET /api/invoices
 */
export function useInvoices(params: InvoiceListParams = {}, options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: invoiceKeys.list(params),
    queryFn: () => fetchInvoices(api, params),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: options?.enabled ?? true,
  });
}

/**
 * Hook to fetch a single invoice by ID
 * GET /api/invoices/:id
 */
export function useInvoice(id: string, options?: { enabled?: boolean }) {
  const api = useBillingApi();
  return useQuery({
    queryKey: invoiceKeys.detail(id),
    queryFn: () => fetchInvoice(api, id),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: (options?.enabled ?? true) && !!id,
  });
}

/**
 * Hook to fetch invoices for a specific client
 * GET /api/invoices/client/:clientId
 */
/** The rate a draft's counter-value in `currency` takes when the user types none. */
export function useCounterValueRate(currency: string | null) {
  const api = useBillingApi();
  return useQuery({
    queryKey: invoiceKeys.counterValueRate(currency ?? ''),
    queryFn: () => fetchCounterValueRate(api, currency!),
    staleTime: 1000 * 60 * 5,
    enabled: currency !== null,
  });
}

export function useInvoicesByClient(
  clientId: string,
  params: { page?: number; limit?: number } = {},
  options?: { enabled?: boolean }
) {
  const api = useBillingApi();
  return useQuery({
    queryKey: invoiceKeys.byClient(clientId),
    queryFn: () => fetchInvoicesByClient(api, clientId, params),
    staleTime: 1000 * 60 * 2, // 2 minutes
    enabled: (options?.enabled ?? true) && !!clientId,
  });
}

// ─────────────────────────────────────────────────────────────
// Mutations
// ─────────────────────────────────────────────────────────────

/**
 * Hook to create a new invoice
 * POST /api/invoices
 */
export function useCreateInvoice() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...OFFLINE_REPLAYED,
    mutationFn: (input: CreateInvoiceInput) => createInvoice(api, input),
    onSuccess: () => {
      // Invalidate invoice lists to refresh data
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Invalidate statistics as they may have changed
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
    },
  });
}

/**
 * Hook to update a draft invoice
 * PUT /api/invoices/:id
 */
export function useUpdateInvoice() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...OFFLINE_REPLAYED,
    mutationFn: ({ id, input }: { id: string; input: UpdateInvoiceInput }) =>
      updateInvoice(api, id, input),
    onSuccess: (updatedInvoice) => {
      // The PUT response carries neither the settlements nor the original
      // invoice number: the detail is read again from GET.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(updatedInvoice.id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to delete a draft invoice
 * DELETE /api/invoices/:id
 */
export function useDeleteInvoice() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...OFFLINE_REPLAYED,
    mutationFn: (id: string) => deleteInvoice(api, id),
    onSuccess: (_, id) => {
      // Remove from detail cache
      queryClient.removeQueries({ queryKey: invoiceKeys.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Invalidate statistics
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
    },
  });
}

/**
 * Hook to sign an invoice via MCF
 * POST /api/invoices/:id/sign
 */
export function useSignInvoice() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (id: string) => signInvoice(api, id),
    // Whether the invoice may now be issued without the MCF is the server's
    // answer to a fresh read (#797), whatever the error.
    onError: (_error, id) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
    },
    onSuccess: (signedInvoice) => {
      // Update the specific invoice in cache
      queryClient.setQueryData(invoiceKeys.detail(signedInvoice.id), signedInvoice);
      // Invalidate lists to reflect the status change
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Invalidate statistics
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      // Signing an invoice deducts stock — see invalidateAfterSale.
      invalidateAfterSale(queryClient);
    },
  });
}

/**
 * Cancels the previous-year number a draft holds after the MCF refused it
 * (#494); the draft then carries its provisional reference.
 * POST /api/invoices/:id/cancel-refused-number (tenant:settings)
 */
export function useCancelRefusedNumber() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ id, reason }: { id: string; reason: string }) => cancelRefusedNumber(api, id, reason),
    onSuccess: (_cancelled, { id }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
    },
  });
}

/**
 * Hook to cancel a settlement of an invoice
 * POST /api/invoices/:id/payments/:paymentId/cancel
 */
export function useCancelPayment() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ id, paymentId, reason }: { id: string; paymentId: string; reason: string }) =>
      cancelPayment(api, id, paymentId, reason),
    onSuccess: (_invoice, { id }) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.overdue() });
    },
  });
}

/**
 * Hook to record a payment for an invoice
 * POST /api/invoices/:id/payment
 */
export function useRecordPayment() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: ({ id, input }: { id: string; input: RecordPaymentInput }) =>
      recordPayment(api, id, input),
    onSuccess: (updatedInvoice) => {
      // Lay the response over the cached invoice, then refetch it: only
      // GET /invoices/:id carries `remainingDue` and `settlementPlacement`,
      // which the cached copy keeps until the refetch lands.
      queryClient.setQueryData<Invoice>(invoiceKeys.detail(updatedInvoice.id), (cached) =>
        cached ? { ...cached, ...updatedInvoice } : updatedInvoice
      );
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(updatedInvoice.id) });
      // An AVOIR payment consumes a credit note's balance.
      queryClient.invalidateQueries({ queryKey: [...invoiceKeys.all, 'applicableCreditNotes'] });
      // Invalidate lists to reflect payment changes
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      // Invalidate statistics
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
      // Invalidate overdue list
      queryClient.invalidateQueries({ queryKey: invoiceKeys.overdue() });
    },
  });
}

/**
 * Hook to download invoice PDF
 * GET /api/invoices/:id/pdf — on a signed invoice the first download is the
 * original and every later one a DUPLICATA (recorded by the API).
 */
export function useDownloadInvoicePdf() {
  const api = useBillingApi();
  return useMutation({
    mutationFn: ({ id, invoiceNumber }: { id: string; invoiceNumber: string }) =>
      downloadInvoicePdf(api, id, invoiceNumber),
  });
}

/**
 * Hook to create a credit note (facture d'avoir) from an existing invoice
 * POST /api/invoices/:id/credit-note
 */
export function useCreateCreditNote() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: async ({
      invoiceId,
      data,
    }: {
      invoiceId: string;
      data: {
        type: 'total' | 'partial';
        /** The nature (COR, RAN, RAM, RRR); absent for a GENERIC_VAT company, which has none (#181). */
        creditNoteType?: CreditNoteType;
        lines: Array<{
          articleId: string;
          articleCode: string;
          articleName: string;
          articleType: string;
          taxGroup: string;
          quantity: number;
          unitPrice: number;
          /** The line of the invoice this line refunds. */
          originalLineId?: string;
        }>;
      };
    }) => {
      return sendBillingMutation<Invoice>(api, 'POST', `/invoices/${invoiceId}/credit-note`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
    },
  });
}

/**
 * Hook to create a standalone RRR credit note (DGI §2.29)
 * POST /api/invoices/credit-notes
 */
export function useCreateStandaloneCreditNote() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: (input: CreateStandaloneCreditNoteInput) =>
      sendBillingMutation<Invoice>(api, 'POST', '/invoices/credit-notes', input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: invoiceKeys.statistics() });
    },
  });
}

/**
 * Response of `POST /api/invoices/:id/duplicate`: the original invoice
 * (same id, same number) carrying the rank of the copy just recorded among
 * this site's copies; another site may hold a copy of the same rank, so the
 * rank is never shown or written without its site.
 */
export type DuplicataRecord = Invoice & { duplicateNumber: number };

/**
 * French reason for a failed duplicata download: the server's message when
 * it answered, a generic one for a network failure (whose browser text is
 * not French).
 */
export function duplicataDownloadReason(cause: unknown): string {
  return cause instanceof ApiError
    ? cause.message
    : 'connexion au serveur impossible, vérifiez le réseau et réessayez';
}

/** A duplicata was recorded but its PDF did not reach the browser. */
export class DuplicataDownloadError extends Error {
  constructor(
    readonly record: DuplicataRecord,
    readonly cause: unknown
  ) {
    super(duplicataDownloadReason(cause));
    this.name = 'DuplicataDownloadError';
  }
}

/**
 * GET /api/invoices/:id/pdf?duplicata=true — renders the latest recorded
 * copy with the DUPLICATA mention; refused while none is recorded.
 */
export async function downloadDuplicataPdf(api: BillingTransport, record: DuplicataRecord): Promise<void> {
  await api.download(
    `/invoices/${record.id}/pdf?duplicata=true`,
    `${record.number}-DUPLICATA.pdf`
  );
}

/**
 * Hook to print a DUPLICATA of a signed invoice (DGI §2.11, §2.18, §3.f):
 * `POST /api/invoices/:id/duplicate` records the copy (no new number, no
 * new invoice), then the duplicata PDF is downloaded. The POST is never
 * queued offline: a copy recorded later would have no printout.
 */
export function useDuplicateInvoice() {
  const api = useBillingApi();
  const queryClient = useQueryClient();

  return useMutation({
    ...NEVER_QUEUED,
    mutationFn: async (invoiceId: string): Promise<DuplicataRecord> => {
      const record = await sendBillingMutation<DuplicataRecord>(api, 'POST', `/invoices/${invoiceId}/duplicate`);
      try {
        await downloadDuplicataPdf(api, record);
      } catch (err) {
        throw new DuplicataDownloadError(record, err);
      }
      return record;
    },
    onSettled: (_data, _error, invoiceId) => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) });
    },
  });
}

// ─────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────

export {
  fetchInvoices,
  fetchInvoice,
  fetchInvoiceByNumber,
  fetchInvoicesByClient,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  signInvoice,
  recordPayment,
  fetchOverdueInvoices,
  downloadInvoicePdf,
};
